/* eslint-disable no-nested-ternary */
import { useState } from 'react'
import { Input, Label } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { PageHeader, LoaderBar, FlexRowBetween } from 'components'
import {
    GetPublicTemplateList,
    GetPublicTemplateTypeDetails,
    GetsortBy,
} from 'hooks'
import { PublicTemplateList, TemplateNameModal } from 'page-components'
import { useNavigate, Link } from 'react-router-dom'
import { TemplateService } from 'utility'
import { AppPrimaryButton } from 'elements'

export const NewTemplateLibrary = () => {
    const [search, setSearch] = useState('')
    const [selectedSort, setSelectedSort] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('')

    // Fetch Public Templates data
    const {
        data: publicTemplates,
        loading: publicLoading,
        filter: publicFilter,
        pageChanged: publicPageChanged,
        totalCount: publicTotalCount,
        filterChanged: publicFilterChanged,
    } = GetPublicTemplateList()

    // Fetch Public Template Categories
    const { data: templateCategories, loading: categoriesLoading } =
        GetPublicTemplateTypeDetails()

    const { data: sortOptions, loading: sortLoading } = GetsortBy()

    const navigate = useNavigate()
    const [processingTemplateId, setProcessingTemplateId] = useState(null)
    const [showTemplateNameModal, setShowTemplateNameModal] = useState(false)
    const [creating, setCreating] = useState(false)

    const handleSearchClick = () => {
        publicFilterChanged({
            SearchText: search.trim(),
        })
    }

    const handleKeyPress = e => {
        if (e.key === 'Enter') {
            handleSearchClick()
        }
    }

    const handleSearchChange = e => {
        const { value } = e.target
        setSearch(value)

        // If the field is cleared, refresh the page
        if (!value.trim()) {
            publicFilterChanged({ SearchText: '' })
        }
    }

    const handleSortChange = (e, { value }) => {
        setSelectedSort(value)
        publicFilterChanged({ SortBy: value })
    }

    const handleCategoryChange = (e, { value }) => {
        setSelectedCategory(value)
        publicFilterChanged({ CategoryId: value || 0 })
    }

    const addToLibrary = async list => {
        try {
            setProcessingTemplateId(list.TemplateId)
            const item = await TemplateService.getPublicTemplateDetails(list.TemplateId, { IsAddToLibrary: true })
            // Navigate to absolute edit route to avoid nested route mismatch
            navigate(`/app/templates/edit/${item.TemplateId}`, { state: { template: item, fromPublic: true } })
        } catch (error) {
            console.log(error)
            setProcessingTemplateId(null)
        } finally {
            // Don't reset here as navigation will unmount the component
        }
    }

    const handleOpenCreateModal = () => {
        setShowTemplateNameModal(true)
    }

    const handleCreateTemplate = async (templateName) => {
        setCreating(true)
        try {
            const result = await TemplateService.create({
                TemplateId: '-1',
                TemplateName: templateName,
                LoginSource: 1,
                TemplateAccess: 'Internal',
            })
            navigate('/app/templates/create', { state: { template: result } })
        } catch (error) {
            console.error('Failed to create template:', error)
        } finally {
            setCreating(false)
            setShowTemplateNameModal(false)
        }
    }

    return (
        <div className="new-template-library px-1 md:px-4">
            <FlexRowBetween className="mb-3">
                <PageHeader header="Public Templates" />
                <span>
                    <AppPrimaryButton onClick={handleOpenCreateModal} className="me-2">
                        Create
                    </AppPrimaryButton>
                    <Link to="/app/templates/create-ai-template">
                        <AppPrimaryButton>
                            Create Template From AI
                        </AppPrimaryButton>
                    </Link>
                </span>
            </FlexRowBetween>
            <div className="md:flex col-2 md:col-3 w-full justify-between flex-wrap">
                <div className="mb-3 md:w-full xl:w-[62%]">
                    {/* Search Input with Icon */}
                    <Input
                        icon={{
                            name: 'search',
                            circular: true,
                            link: true,
                            onClick: handleSearchClick,
                            className:
                                'bg-blue-800 text-white p-2 rounded-full hover:bg-blue-600',
                        }}
                        placeholder="Search Public Templates"
                        className="w-full"
                        value={search}
                        onChange={handleSearchChange}
                        onKeyPress={handleKeyPress}
                    />
                </div>

                {/* Filters for Category and Sort by */}
                <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-4 mb-3 sm:mb-3 xl:w-[37%] justify-between gap-x-4">
                    <Dropdown
                        placeholder="Filter by Category"
                        selection
                        search
                        options={templateCategories}
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        loading={categoriesLoading}
                        className="sm:gap-x-0 sm:w-full sm:h-[36px]"
                        clearable
                    />

                    <Dropdown
                        placeholder="Sort by"
                        selection
                        search
                        options={sortOptions}
                        value={selectedSort}
                        onChange={handleSortChange}
                        className="sm:w-full sm:h-[36px]"
                        loading={sortLoading}
                        clearable
                    />
                </div>

                {/* Display selected categories below the filter */}
                {selectedCategory && (
                    <div className="mb-3">
                        <Label>
                            <b>Selected Category:</b>{' '}
                            {
                                templateCategories.find(
                                    c => c.value === selectedCategory,
                                )?.text
                            }
                        </Label>
                    </div>
                )}
            </div>

            {publicLoading ? (
                <LoaderBar content="Loading Public Templates..." />
            ) : publicTemplates.length > 0 ? (
                <PublicTemplateList
                    data={publicTemplates}
                    pageChanged={publicPageChanged}
                    totalCount={publicTotalCount}
                    filter={publicFilter}
                    addToLibrary={addToLibrary}
                    processingTemplateId={processingTemplateId}
                />
            ) : (
                <p className="text-gray-500">No Public Templates Found</p>
            )}
            <TemplateNameModal
                open={showTemplateNameModal}
                onClose={() => setShowTemplateNameModal(false)}
                onSubmit={handleCreateTemplate}
                loading={creating}
            />
        </div>
    )
}

export default NewTemplateLibrary
